import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Level, Logger } from '@nsalaun/ng-logger';
import { install as installResizeObserverPolyfill } from 'resize-observer';
import { map } from 'rxjs/operators';

import { DialogLevel, InfoDialogData, StandardDialogService } from '@svg-frontends/dialog';
import { EnrichedError, ErrorHandlingService } from '@svg-frontends/error';
import { LoggingService } from '@svg-frontends/logging';
import { AsiContext, AsiContextService, AuthRefreshService, KeycloakTokenClaims } from '@svg-frontends/session';
import { CustomCommonStringsService } from '@svg-frontends/theming';
import { UpdateAppWorkerService } from '@svg-frontends/update-app-worker';

import '../../../../init-dayjs';
import { ENVIRONMENT } from '../environments/environment';
import { ClarityInitService } from './clarity-init.service';

@UntilDestroy()
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-root',
	styleUrls: ['./app.component.scss'],
	templateUrl: './app.component.html',
	standalone: false,
})
export class AppComponent {
	constructor(
		asiContextService: AsiContextService,
		authRefreshService: AuthRefreshService<AsiContext, KeycloakTokenClaims>,
		clarityInitService: ClarityInitService,
		customCommonStringsService: CustomCommonStringsService,
		loggingService: LoggingService,
		logger: Logger,
		updateAppWorkerService: UpdateAppWorkerService,
		private errorHandlingService: ErrorHandlingService,
		private standardDialogService: StandardDialogService,
	) {
		authRefreshService.init();
		clarityInitService.init();
		asiContextService.init();
		customCommonStringsService.init();
		loggingService.init();
		logger.info(`Started myASI application and enabled Logging with Level ${Level[logger.level]}`);

		this.initErrorDialogHandling();

		if (ENVIRONMENT.enabledUpdateServiceWorker) {
			updateAppWorkerService.init();
			logger.debug(
				"UpdateAppWorkerService initialized and actively checking for updates in the background to ensure you're always running the latest version.",
			);
		} else {
			logger.debug('Failed to initialize UpdateAppWorkerService. Unable to check for updates in the background');
		}

		// polyfill for older IOS devices not supporting ResizeObserver (WEB-2140)
		if (!window.ResizeObserver) installResizeObserverPolyfill();
	}

	private static getInfoDialogDataFromError(enrichedError: EnrichedError): InfoDialogData {
		return {
			body: enrichedError.displayMessages.join(' '),
			level: DialogLevel.DANGER,
			submitButton: 'Ok',
			title: 'Ups, es ist ein Fehler aufgetreten!',
		};
	}

	private initErrorDialogHandling(): void {
		this.errorHandlingService
			.getErrorForDialogObservable()
			.pipe(
				map((enrichedError: EnrichedError) => AppComponent.getInfoDialogDataFromError(enrichedError)),
				untilDestroyed(this),
			)
			.subscribe((data: InfoDialogData) => this.standardDialogService.showInfoDialog(data));
	}
}
